import { SVGIcon } from '@/components_deprecated'
import type { DriverActivityReport } from '@/modules/activities/types.graphql'
import { Module, ModuleOptionsValue } from '@/modules/companies/consts'
import type { ActivitiesModuleConfig } from '@/modules/companies/types.graphql'
import { format } from '@/utils/date-fns'
import { useModuleConfig } from '@/utils/hooks'
import { overloadColor } from '@/utils/style'
import { useTranslate } from '@ur-solutions/react-hooks'
import styled from 'styled-components'
import { enforceHourMinuteString } from '../../util'
import { AllActivitiesSumTable } from './AllActivitiesSumTable'
import { H2 } from '@/components'
import { Icon } from '@ur-solutions/react-components'

const StyledIcon = styled(Icon)`
  font-weight: 300 !important;
`

const Summaries = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem 1.5rem;

  ${props => props.theme.media.custom({ min: '769px', max: '1720px' })} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem 1.5rem;
  }

  h2 {
    grid-column: 1 / -1;
    display: flex;

    margin: 1rem 0 0 8px;
    font-weight: 600;

    div {
      margin-left: 1rem;
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;
  }
`
interface SummaryProps {
  color: string
}
const Summary = styled.div<SummaryProps>`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  padding: 2rem;

  cursor: default;

  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background-color: white;
  box-shadow: ${props => props.theme.shadow.default};

  div.icon {
    display: flex;
    justify-content: center;
    align-items: center;

    width: 6rem;
    height: 6rem;

    border-radius: 20px;
    background: ${props => overloadColor(props.color)};

    svg {
      width: 60%;
    }
    i {
      font-size: 3rem;
      font-weight: 100;
    }
  }
  div.info {
    display: flex;
    flex-direction: column;
    justify-content: center;

    span {
      font-weight: 600;

      &.label {
        color: ${props => props.theme.colors.gray4};
      }
      &.time {
        font-size: 2rem;
        p {
          font-size: 1rem;
          color: ${props => props.theme.colors.gray4};
          margin: 0;
        }
      }
    }
  }

  ${props => props.theme.media.custom({ min: '800px', max: '1500px' })} {
    gap: 1.5rem;
    padding: 1.5rem;

    div.icon {
      width: 5rem;
      height: 5rem;

      border-radius: 16px;

      i {
        font-size: 2.5rem;
      }
    }
  }
  ${props => props.theme.media.mobile} {
    gap: 1rem;
    padding: 1rem;

    div.icon {
      width: 4rem;
      height: 4rem;

      border-radius: 12px;

      i {
        font-size: 2rem;
      }
    }
  }
`

interface ActivitiesMonthSummariesProps {
  month: Date
  showOvertime?: boolean
  report: DriverActivityReport
}

export const ActivitiesMonthSummaries: React.JSX.CFC<
  ActivitiesMonthSummariesProps
> = ({ month, showOvertime, report }) => {
  const translations = useTranslate({
    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    pause: 'common.pause',
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'activities.sum-overtime',
    available: 'activities.availability-time',
    plusNNextMonth: ['activities.plus-n-next-month', { n: '' }],
  })
  const salaryModuleActive = useModuleConfig(Module.SALARY).moduleActive

  const summaryOptions = {
    pause: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_PAUSE,
    availability: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_AVAILABILITY_TIME,
    driving: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_DRIVING,
    otherWork: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_OTHER_WORK,
    overtime: ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_TOTAL_OVERTIME,
  }

  const { getModuleOptionStatus } = useModuleConfig<ActivitiesModuleConfig>(
    Module.ACTIVITIES
  )

  const summaryStatus = Object.fromEntries(
    Object.entries(summaryOptions).map(([key, value]) => [
      key,
      getModuleOptionStatus(value),
    ])
  ) as Record<
    keyof typeof summaryOptions,
    ReturnType<typeof getModuleOptionStatus>
  >

  const overtime = report?.overtime

  return (
    <>
      <Summaries>
        <H2>{translations.sumForMonth({ month: format(month, 'MMMM') })}</H2>

        {summaryStatus.driving && (
          <Summary color="green600">
            <div className="icon">
              <SVGIcon icon="truck" />
            </div>
            <div className="info">
              <span className="label">{translations.driving}</span>
              <span className="time">
                {enforceHourMinuteString(report?.timeDriving) ?? '-'}
              </span>
            </div>
          </Summary>
        )}

        {summaryStatus.otherWork && (
          <Summary color="orange">
            <div className="icon">
              {/* TODO not correct icon */}
              <StyledIcon icon="briefcase" fixedWidth />
            </div>
            <div className="info">
              <span className="label">{translations.otherWork}</span>
              <span className="time">
                {enforceHourMinuteString(report?.timeOtherWork) ?? '-'}
              </span>
            </div>
          </Summary>
        )}

        {summaryStatus.availability && (
          <Summary color="secondary">
            <div className="icon">
              {/* TODO not correct icon */}
              <StyledIcon icon="hand-holding" fixedWidth translateY="-0.8rem" />
            </div>
            <div className="info">
              <span className="label">{translations.available}</span>
              <span className="time">
                {enforceHourMinuteString(report?.timeAvailability) ?? '-'}
              </span>
            </div>
          </Summary>
        )}
        {summaryStatus.pause && (
          <Summary color="blue400">
            <div className="icon">
              <StyledIcon icon="pause" fixedWidth />
            </div>
            <div className="info">
              <span className="label">{translations.pause}</span>
              <span className="time">
                {enforceHourMinuteString(report?.timePause) ?? '-'}
              </span>
            </div>
          </Summary>
        )}
        {showOvertime && summaryStatus.overtime && (
          <Summary color="#fabbce">
            <div className="icon">
              <SVGIcon icon="clock" />
            </div>
            <div className="info">
              <span className="label">{translations.overtime}</span>
              <span className="time">
                {enforceHourMinuteString(overtime) ?? '-'}
                {report?.salaryWorkTime.useWeekOvertime &&
                  report?.overtimeNextMonth !== '00:00' && (
                    <p>
                      {translations.plusNNextMonth({
                        n: enforceHourMinuteString(report?.overtimeNextMonth),
                      })}
                    </p>
                  )}
              </span>
            </div>
          </Summary>
        )}
      </Summaries>
      {!salaryModuleActive && <AllActivitiesSumTable report={report} />}
    </>
  )
}
